import { logger } from '../../logging/logger'
import type { StripeOnrampClient } from './stripeOnramp'

/**
 * Reads the contract's silent sign-in credential off a step response.
 *
 * It rides `FiatStepResponseBase`, not a surface: the backend mints it beside the pay sheet so the
 * client can wake its Link session without a round trip of its own. Absent means no silent path.
 */
export function stripeSeamlessClientSecret(
  response: object | undefined,
  now: number = Date.now(),
): string | undefined {
  const token = (response as { providerAuthToken?: unknown } | undefined)
    ?.providerAuthToken
  if (token === null || typeof token !== 'object') {
    return undefined
  }
  const { clientSecret, expiresAt } = token as {
    clientSecret?: unknown
    expiresAt?: unknown
  }
  if (typeof clientSecret !== 'string' || clientSecret.length === 0) {
    return undefined
  }
  // A lapsed credential is not worth redeeming — the SDK would refuse it and the interactive round
  // is the fallback either way. An unreadable `expiresAt` counts as live: discarding a good token
  // over a format we failed to parse costs a sign-in, and the SDK is the real authority.
  if (typeof expiresAt === 'string') {
    const expiresAtMs = Date.parse(expiresAt)
    if (Number.isFinite(expiresAtMs) && expiresAtMs <= now) {
      logger.info('connect-rn:fiat:stripe_seamless_token_expired', {
        expiresAt,
      })
      return undefined
    }
  }
  return clientSecret
}

/**
 * Redeems the credential against the device's SDK. Never throws.
 *
 * The SDK holds a Link consumer session separate from the grant our backend stores, and the two
 * diverge — see `logOut` on `StripeOnrampClient`. Redeeming here wakes that session before
 * `registerWalletAddress` asks for it, which is the failure this exists to prevent. It stays an
 * optimisation: a refusal means the buyer signs in interactively, not that payment stops.
 */
export async function redeemStripeSeamlessSignIn(
  stripe: StripeOnrampClient,
  clientSecret: string,
): Promise<boolean> {
  try {
    const result = await stripe.authenticateUserWithToken(clientSecret)
    const authenticated = result.error === undefined
    logger.info('connect-rn:fiat:stripe_seamless_signin_returned', {
      authenticated,
      ...(result.error ? { code: result.error.code } : {}),
    })
    return authenticated
  } catch (error: unknown) {
    logger.warn('connect-rn:fiat:stripe_seamless_signin_threw', {
      error: error instanceof Error ? error.message : String(error),
    })
    return false
  }
}
